// MOTION LAB — Laboratoire interne du langage d'animation WYVIO.
// Route : /motion-lab (protégée, accessible aux agents connectés uniquement).
// N'est PAS une page utilisateur. Sert à valider et documenter les presets.
// Toute animation créée ailleurs dans WYVIO doit passer par les mêmes tokens.

import { useState, useMemo } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import {
  duration,
  easing,
  spring,
  distance,
  stagger,
  fade,
  slide,
  slideRight,
  scale,
  cardHover,
  buttonPress,
  drawerTransition,
  drawerBackdrop,
  modalTransition,
  heroTransition,
  sharedTransition,
  staggerContainer,
  staggerItem,
  pageTransition,
  useMotionSafe,
} from "@/motion";

/* ═════════════════════════ Section wrapper ═════════════════ */
const Section = ({ eyebrow, title, subtitle, children }) => (
  <section className="border-t border-black/5 py-16">
    <div className="mb-10 max-w-2xl">
      {eyebrow && (
        <div className="mb-3 text-[11px] uppercase tracking-[0.14em] text-black/40">
          {eyebrow}
        </div>
      )}
      <h2 className="text-[28px] font-medium tracking-[-0.02em] text-black">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-2 text-[15px] leading-relaxed text-black/50">
          {subtitle}
        </p>
      )}
    </div>
    {children}
  </section>
);

/* ═════════════════════════ Chip ────────────────────────────── */
const Chip = ({ label, value }) => (
  <div className="flex items-baseline gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5">
    <span className="text-[11px] uppercase tracking-[0.1em] text-black/40">
      {label}
    </span>
    <span className="font-mono text-[12px] text-black">{value}</span>
  </div>
);

/* ═════════════════════════ Replay button ═══════════════════ */
const Replay = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    variants={buttonPress}
    initial="rest"
    whileHover="hover"
    whileTap="tap"
    className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3.5 py-1.5 text-[12px] font-medium text-black transition-colors hover:bg-black hover:text-white"
    data-testid="motion-lab-replay-btn"
  >
    <span>↻</span> Rejouer
  </motion.button>
);

/* ═════════════════════════ Preset demo card ════════════════ */
const PresetCard = ({ name, description, code, children, onReplay }) => (
  <div className="rounded-2xl border border-black/8 bg-white p-6">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-black">{name}</div>
        <div className="mt-0.5 text-[12px] text-black/50">{description}</div>
      </div>
      {onReplay && <Replay onClick={onReplay} />}
    </div>
    <div className="relative flex h-40 items-center justify-center overflow-hidden rounded-xl bg-[#F8F8F5]">
      {children}
    </div>
    {code && (
      <pre className="mt-4 overflow-x-auto rounded-lg bg-black/[0.03] px-3 py-2 font-mono text-[11px] leading-relaxed text-black/60">
        {code}
      </pre>
    )}
  </div>
);

/* ═════════════════════════ Easing preview (SVG) ════════════ */
const EasingCurve = ({ name, curve }) => {
  const [p1x, p1y, p2x, p2y] = curve;
  const d = `M 0 100 C ${p1x * 100} ${100 - p1y * 100}, ${p2x * 100} ${100 - p2y * 100}, 100 0`;
  return (
    <div className="rounded-2xl border border-black/8 bg-white p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-[13px] font-medium text-black">{name}</div>
        <div className="font-mono text-[10px] text-black/40">
          [{curve.map((n) => n.toFixed(2)).join(", ")}]
        </div>
      </div>
      <svg viewBox="-4 -4 108 108" className="h-32 w-full">
        <line x1="0" y1="100" x2="100" y2="100" stroke="rgba(0,0,0,0.08)" />
        <line x1="0" y1="0" x2="0" y2="100" stroke="rgba(0,0,0,0.08)" />
        <path d={d} fill="none" stroke="black" strokeWidth="1.5" />
      </svg>
    </div>
  );
};

/* ═════════════════════════ Fade / Slide / Scale demos ══════ */
const AnimatedBlock = ({ variants, keyId }) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={keyId}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="h-16 w-32 rounded-xl bg-black"
    />
  </AnimatePresence>
);

/* ═════════════════════════ Drawer demo ─────────────────────── */
const DrawerDemo = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <motion.button
        variants={buttonPress}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onClick={() => setOpen(true)}
        className="rounded-full bg-black px-4 py-2 text-[12px] font-medium text-white"
        data-testid="motion-lab-drawer-open-btn"
      >
        Ouvrir le drawer
      </motion.button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              variants={drawerBackdrop}
              initial="initial"
              animate="animate"
              exit="exit"
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
            />
            <motion.aside
              variants={drawerTransition}
              initial="initial"
              animate="animate"
              exit="exit"
              className="fixed right-0 top-0 z-50 h-full w-[420px] max-w-[92vw] bg-white p-8 shadow-xl"
            >
              <div className="text-[11px] uppercase tracking-[0.14em] text-black/40">
                Drawer
              </div>
              <h3 className="mt-3 text-[24px] font-medium tracking-[-0.02em] text-black">
                Panneau latéral.
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-black/60">
                Glissement naturel, fin très douce. Backdrop flouté.
              </p>
              <button
                onClick={() => setOpen(false)}
                className="mt-8 text-[13px] font-medium text-black underline underline-offset-4"
                data-testid="motion-lab-drawer-close-btn"
              >
                Fermer
              </button>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

/* ═════════════════════════ Modal demo ─────────────────────── */
const ModalDemo = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <motion.button
        variants={buttonPress}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onClick={() => setOpen(true)}
        className="rounded-full bg-black px-4 py-2 text-[12px] font-medium text-white"
        data-testid="motion-lab-modal-open-btn"
      >
        Ouvrir le modal
      </motion.button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              variants={drawerBackdrop}
              initial="initial"
              animate="animate"
              exit="exit"
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                variants={modalTransition}
                initial="initial"
                animate="animate"
                exit="exit"
                className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl"
              >
                <h3 className="text-[22px] font-medium tracking-[-0.02em] text-black">
                  Confirmer la livraison.
                </h3>
                <p className="mt-2 text-[14px] text-black/50">
                  Cette action est irréversible.
                </p>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setOpen(false)}
                    className="rounded-full px-4 py-2 text-[13px] font-medium text-black/60 hover:text-black"
                  >
                    Annuler
                  </button>
                  <motion.button
                    variants={buttonPress}
                    initial="rest"
                    whileHover="hover"
                    whileTap="tap"
                    onClick={() => setOpen(false)}
                    className="rounded-full bg-[#C8FF2F] px-4 py-2 text-[13px] font-medium text-black"
                  >
                    Confirmer
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

/* ═════════════════════════ Hero (layoutId) demo ───────────── */
const HeroDemo = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <LayoutGroup>
      <AnimatePresence mode="wait">
        {!expanded ? (
          <motion.div
            key="card"
            layoutId="hero-demo"
            onClick={() => setExpanded(true)}
            transition={heroTransition}
            className="flex h-32 w-56 cursor-pointer flex-col justify-between rounded-2xl bg-black p-4"
            data-testid="motion-lab-hero-card"
          >
            <motion.div layoutId="hero-title" className="text-[11px] uppercase tracking-[0.14em] text-white/50">
              Dossier
            </motion.div>
            <motion.div layoutId="hero-heading" className="text-[18px] font-medium text-white">
              Porsche 911 GT3
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="expanded"
            layoutId="hero-demo"
            onClick={() => setExpanded(false)}
            transition={heroTransition}
            className="flex h-full w-full cursor-pointer flex-col justify-between rounded-2xl bg-black p-8"
            data-testid="motion-lab-hero-expanded"
          >
            <motion.div layoutId="hero-title" className="text-[11px] uppercase tracking-[0.14em] text-white/50">
              Fiche dossier
            </motion.div>
            <motion.div layoutId="hero-heading" className="text-[32px] font-medium tracking-[-0.02em] text-white">
              Porsche 911 GT3
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LayoutGroup>
  );
};

/* ═════════════════════════ Card hover demo ────────────────── */
const CardHoverDemo = () => (
  <motion.div
    variants={cardHover}
    initial="rest"
    whileHover="hover"
    whileTap="tap"
    className="w-56 cursor-pointer rounded-2xl border border-black/8 bg-white p-5"
    data-testid="motion-lab-card-hover"
  >
    <div className="text-[11px] uppercase tracking-[0.14em] text-black/40">
      Carte
    </div>
    <div className="mt-2 text-[16px] font-medium text-black">
      Passe la souris.
    </div>
    <div className="mt-1 text-[12px] text-black/50">
      Élévation 2 px · spring stiff
    </div>
  </motion.div>
);

/* ═════════════════════════ Button demo ────────────────────── */
const ButtonPressDemo = () => (
  <div className="flex flex-wrap items-center justify-center gap-3">
    <motion.button
      variants={buttonPress}
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      className="rounded-full bg-black px-5 py-2.5 text-[13px] font-medium text-white"
      data-testid="motion-lab-btn-primary"
    >
      Action primaire
    </motion.button>
    <motion.button
      variants={buttonPress}
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      className="rounded-full bg-[#C8FF2F] px-5 py-2.5 text-[13px] font-medium text-black"
      data-testid="motion-lab-btn-jewel"
    >
      Récompense
    </motion.button>
    <motion.button
      variants={buttonPress}
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      className="rounded-full border border-black/10 bg-white px-5 py-2.5 text-[13px] font-medium text-black"
      data-testid="motion-lab-btn-ghost"
    >
      Secondaire
    </motion.button>
  </div>
);

/* ═════════════════════════ Stagger list demo ─────────────── */
const StaggerDemo = ({ keyId }) => (
  <motion.ul
    key={keyId}
    variants={staggerContainer}
    initial="initial"
    animate="animate"
    exit="exit"
    className="w-full max-w-sm space-y-2"
  >
    {["Peugeot 208", "BMW M2", "Porsche 911 GT3", "Renault Clio V"].map((v) => (
      <motion.li
        key={v}
        variants={staggerItem}
        className="rounded-lg border border-black/8 bg-white px-4 py-2.5 text-[13px] text-black"
      >
        {v}
      </motion.li>
    ))}
  </motion.ul>
);

/* ═════════════════════════ MAIN PAGE ═══════════════════════ */
export default function MotionLab() {
  const [fadeKey, setFadeKey] = useState(0);
  const [slideKey, setSlideKey] = useState(0);
  const [slideRKey, setSlideRKey] = useState(0);
  const [scaleKey, setScaleKey] = useState(0);
  const [staggerKey, setStaggerKey] = useState(0);
  const reduced = useMotionSafe();

  return (
    <div className="min-h-screen bg-[#F8F8F5] text-black" data-testid="motion-lab-page">
      {/* Header */}
      <header className="border-b border-black/5 bg-white/60 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-baseline justify-between px-6 py-10 sm:px-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-black/40">
              WYVIO Motion Lab
            </div>
            <h1 className="mt-3 text-[44px] font-medium tracking-[-0.03em] text-black">
              Le langage d'animation.
            </h1>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-black/50">
              Un seul système. Mêmes courbes, mêmes timings, partout. Ce
              laboratoire définit ce qui peut être utilisé dans WYVIO — rien
              d'autre.
            </p>
          </div>
          <div className="hidden shrink-0 flex-col items-end gap-1.5 sm:flex">
            <Chip label="Statut" value={reduced ? "reduced-motion actif" : "motion complet"} />
            <Chip label="Framer" value="v11" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 sm:px-10">
        {/* ── Tokens : durées ── */}
        <Section
          eyebrow="01 — Fondations"
          title="Durées."
          subtitle="Quatre durées seulement. Toute autre valeur est interdite."
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(duration).map(([k, v]) => (
              <div
                key={k}
                className="rounded-2xl border border-black/8 bg-white p-6"
              >
                <div className="text-[11px] uppercase tracking-[0.14em] text-black/40">
                  {k}
                </div>
                <div className="mt-2 font-mono text-[26px] font-medium text-black">
                  {(v * 1000).toFixed(0)}
                  <span className="ml-1 text-[13px] text-black/40">ms</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Tokens : courbes ── */}
        <Section
          eyebrow="02 — Fondations"
          title="Courbes."
          subtitle="Trailing très douce, entrée nette. Style Apple. Aucun rebond cartoon."
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(easing)
              .filter(([k]) => k !== "linear")
              .map(([k, v]) => (
                <EasingCurve key={k} name={k} curve={v} />
              ))}
          </div>
        </Section>

        {/* ── Presets d'apparition ── */}
        <Section
          eyebrow="03 — Presets"
          title="Apparitions."
          subtitle="Trois manières d'apparaître. Rien de plus."
        >
          <div className="grid gap-4 lg:grid-cols-3">
            <PresetCard
              name="fade"
              description="Le plus discret. Pour les changements d'état."
              code={`initial: opacity 0\nanimate: opacity 1\n${(duration.base * 1000).toFixed(0)}ms · premium`}
              onReplay={() => setFadeKey((k) => k + 1)}
            >
              <AnimatedBlock variants={fade} keyId={fadeKey} />
            </PresetCard>

            <PresetCard
              name="slide"
              description="Apparition standard — cartes, sections."
              code={`y: 8 → 0 · opacity 0 → 1\n${((duration.base + 0.04) * 1000).toFixed(0)}ms · premium`}
              onReplay={() => setSlideKey((k) => k + 1)}
            >
              <AnimatedBlock variants={slide} keyId={slideKey} />
            </PresetCard>

            <PresetCard
              name="scale"
              description="Popover, toolbar — l'élément arrive."
              code={`scale: 0.96 → 1 · opacity 0 → 1\n${(duration.base * 1000).toFixed(0)}ms · premium`}
              onReplay={() => setScaleKey((k) => k + 1)}
            >
              <AnimatedBlock variants={scale} keyId={scaleKey} />
            </PresetCard>
          </div>
        </Section>

        {/* ── Interactions ── */}
        <Section
          eyebrow="04 — Interactions"
          title="Réponse tactile."
          subtitle="Feedback immédiat, spring physique. Jamais de délai perceptible."
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <PresetCard
              name="cardHover"
              description="Élévation 2 px · spring stiff · ombre plus profonde."
              code={`whileHover: y -2\nboxShadow: 0 8px 24px rgba(0,0,0,.06)`}
            >
              <CardHoverDemo />
            </PresetCard>

            <PresetCard
              name="buttonPress"
              description="Hover scale 1.01 · Tap scale 0.98 · spring stiff."
              code={`whileHover: scale 1.01\nwhileTap: scale 0.98`}
            >
              <ButtonPressDemo />
            </PresetCard>
          </div>
        </Section>

        {/* ── Overlays ── */}
        <Section
          eyebrow="05 — Overlays"
          title="Drawer & modal."
          subtitle="Glissement naturel, backdrop flouté, fin très douce."
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <PresetCard
              name="drawerTransition"
              description="Panneau latéral droit · backdrop blur."
              code={`x: 100% → 0\n${(duration.slow * 1000).toFixed(0)}ms · premium`}
            >
              <DrawerDemo />
            </PresetCard>

            <PresetCard
              name="modalTransition"
              description="Centre + scale léger · backdrop blur."
              code={`scale: 0.98 → 1\ny: 8 → 0`}
            >
              <ModalDemo />
            </PresetCard>
          </div>
        </Section>

        {/* ── Hero shared ── */}
        <Section
          eyebrow="06 — Continuité"
          title="Hero transition."
          subtitle="La carte s'agrandit en fiche. Le même écran, réorganisé. layoutId partagé."
        >
          <PresetCard
            name="heroTransition"
            description="Clic sur la carte → clic pour revenir. Framer layoutId."
            code={`layoutId="dossier-{id}"\nspring soft · ${(duration.hero * 1000).toFixed(0)}ms`}
          >
            <HeroDemo />
          </PresetCard>
        </Section>

        {/* ── Stagger ── */}
        <Section
          eyebrow="07 — Listes"
          title="Stagger."
          subtitle="Les enfants apparaissent en cascade. Impression de vie sans lourdeur."
        >
          <PresetCard
            name="staggerContainer + staggerItem"
            description={`Délai ${(stagger.base * 1000).toFixed(0)}ms entre chaque enfant.`}
            code={`staggerChildren: 0.05\ndelayChildren: 0.02`}
            onReplay={() => setStaggerKey((k) => k + 1)}
          >
            <StaggerDemo keyId={staggerKey} />
          </PresetCard>
        </Section>

        {/* ── slideRight ── */}
        <Section
          eyebrow="08 — Variations"
          title="Slide horizontal."
          subtitle="Pour éléments qui arrivent latéralement — chip, badge, notif."
        >
          <PresetCard
            name="slideRight"
            description="x: -8 → 0 · opacity 0 → 1."
            code={`x: -8 → 0\n${(duration.base * 1000).toFixed(0)}ms · premium`}
            onReplay={() => setSlideRKey((k) => k + 1)}
          >
            <AnimatedBlock variants={slideRight} keyId={slideRKey} />
          </PresetCard>
        </Section>

        {/* ── Règles ── */}
        <Section
          eyebrow="09 — Règles"
          title="Les interdits."
          subtitle="Ce qui n'a pas sa place dans WYVIO."
        >
          <ul className="grid gap-2 text-[14px] text-black/60 sm:grid-cols-2">
            {[
              "Animations > 500 ms sauf hero transition",
              "Rebond cartoon (spring stiffness < 150)",
              "Glassmorphism en fond permanent",
              "Gradients multicolores dans les motions",
              "Lumières excessives, halo, glow",
              "Transitions différentes par page",
              "Valeurs de durée hors tokens",
              "Animations non-GPU (top / left / margin)",
            ].map((l) => (
              <li
                key={l}
                className="flex items-start gap-2 rounded-lg border border-black/6 bg-white px-3 py-2"
              >
                <span className="mt-0.5 text-black/30">×</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* ── Footer ── */}
        <footer className="border-t border-black/5 py-16 text-center">
          <div className="text-[11px] uppercase tracking-[0.16em] text-black/40">
            WYVIO Motion System · v1
          </div>
          <p className="mx-auto mt-4 max-w-xl text-[14px] leading-relaxed text-black/50">
            Ce document est la source unique. Toute animation créée en dehors de
            ces presets sera rejetée en revue design.
          </p>
        </footer>
      </main>
    </div>
  );
}

// Silence lint (variants unused externally kept for exhaustive doc)
void sharedTransition;
void pageTransition;
